import { PanelId, PanelLayout, PANELS, DEFAULT_ORDER, locksLeaveRoom } from './registry';

// The panel layout, kept between sessions.
//
// The current layout is saved as it changes and restored on the next launch.
// It is the same plain object the app keeps (PanelLayout), which is also the
// shape a user's saved layouts and the app's presets will take: those will be
// further entries beside `current` in the same stored document.
//
// A stored layout is never trusted as is. The panel list changes between
// versions, and a layout saved by one version has to come back sensibly in
// the next, so readLayout repairs rather than rejects: unknown panels are
// dropped, panels it does not mention come back switched off, locks that no
// longer leave room come off, and a start that is not in the stack is
// replaced. Anything that cannot be read at all falls back to the default.
//
// localStorage, not Firestore: it needs no account, works offline, and works
// unchanged in the Electron build. It belongs to this browser only.

const STORAGE_KEY = 'harmona.panelLayout';
const STORAGE_VERSION = 1;

const isPanelId = (value: unknown): value is PanelId =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(PANELS, value);

/** Known panel ids from a stored list, in its order, each once. */
function panelIds(value: unknown): PanelId[] {
  if (!Array.isArray(value)) return [];
  const ids: PanelId[] = [];
  value.forEach(v => {
    if (isPanelId(v) && ids.indexOf(v) === -1) ids.push(v);
  });
  return ids;
}

/**
 * Make a usable layout out of whatever was stored, or null when there is
 * nothing to work with.
 */
export function readLayout(raw: unknown): PanelLayout | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const stored = raw as { order?: unknown; hidden?: unknown; locked?: unknown; start?: unknown };
  const known = panelIds(stored.order);
  if (known.length === 0) return null;

  // Panels added since this was saved come back off, at the end.
  const added = DEFAULT_ORDER.filter(p => known.indexOf(p) === -1);
  const order = known.concat(added);
  const hidden = panelIds(stored.hidden).concat(added);

  // Locks are a pile, so they are kept from the left and the first that is
  // switched off or no longer leaves room ends it.
  const locked: PanelId[] = [];
  const storedLocks = panelIds(stored.locked);
  for (let i = 0; i < storedLocks.length; i++) {
    const p = storedLocks[i];
    if (hidden.indexOf(p) !== -1 || !locksLeaveRoom(order, locked.concat([p]))) break;
    locked.push(p);
  }

  const stack = order.filter(p => hidden.indexOf(p) === -1 && locked.indexOf(p) === -1);
  let start: PanelId = stack.length > 0 ? stack[0] : order[0];
  if (isPanelId(stored.start) && stack.indexOf(stored.start) !== -1) {
    start = stored.start;
  }
  return { order: order, hidden: hidden, locked: locked, start: start };
}

/** The layout saved last time, repaired for this version, or null for none. */
export function loadLayout(): PanelLayout | null {
  try {
    const text = window.localStorage.getItem(STORAGE_KEY);
    if (text === null) return null;
    const doc = JSON.parse(text);
    return readLayout(doc && doc.current);
  } catch (e) {
    // Storage unavailable or unreadable — start from the default.
    return null;
  }
}

/** Save the current layout. */
export function saveLayout(layout: PanelLayout): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: STORAGE_VERSION, current: layout }));
  } catch (e) {
    // Storage unavailable (private mode, quota) — the layout lasts this session only.
  }
}
